import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { I } from '../Icons'

const Diff = styled.div`
  margin-top: 10px;
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  overflow: hidden;
`

const DiffHead = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${p => p.theme.colors.semantic.surfaceRaised};
`

const DiffHeadT = styled.span`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const RevTag = styled.span`
  margin-left: auto;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.theme.colors.semantic.accent};
  padding: 2px 8px;
  background: ${p => p.theme.colors.semantic.lilacTint};
  border-radius: 4px;
`

const DiffBody = styled.div`
  padding: 4px 0;
`

const DiffField = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  &:last-child {
    border-bottom: none;
  }
`

const K = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.semantic.textMuted};
  font-weight: 700;
  margin-bottom: 4px;
`

const Old = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.textMuted};
  text-decoration: line-through;
  padding: 4px 7px;
  background: ${p => p.theme.colors.semantic.surfaceMuted};
  border-radius: 4px;
  margin-bottom: 4px;
  line-height: 1.45;
`

const New = styled.div`
  font-size: 12px;
  padding: 4px 7px;
  background: ${p => p.theme.colors.semantic.successTint};
  color: ${p => p.theme.colors.semantic.textPrimary};
  border-radius: 4px;
  line-height: 1.5;
`

const LongNew = styled(New)`
  max-height: 100px;
  overflow: hidden;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 24px;
    background: linear-gradient(180deg, transparent, ${p => p.theme.colors.semantic.successTint});
  }
`

const DiffActions = styled.div`
  padding: 10px 12px;
  border-top: 1px solid ${p => p.theme.colors.semantic.border};
  display: flex;
  gap: 6px;
  align-items: center;
  background: ${p => p.theme.colors.semantic.surfaceRaised};
`

const SubBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  background: ${p => p.theme.colors.semantic.accent};
  color: #fff;
  border: none;
  height: 32px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.accentHover};
  }
`

const SecBtn = styled.button`
  background: transparent;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  color: ${p => p.theme.colors.semantic.textPrimary};
  height: 32px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
`

const DiscBtn = styled(SecBtn)`
  color: ${p => p.theme.colors.semantic.textMuted};
`

const SubmittedRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.success};
  font-weight: 600;
`

const WipAiDiffCard = ({
  changes,
  producedRev = null,
  nextRevNumber,
  onSubmit,
  onDiscard,
  submitted,
  producedRevLabel,
}) => (
  <Diff>
    <DiffHead>
      <I.flask width={12} height={12} style={{ color: '#4241BF' }} />
      <DiffHeadT>Proposed Changes</DiffHeadT>
      <RevTag>→ Rev {submitted ? producedRevLabel : nextRevNumber}</RevTag>
    </DiffHead>
    <DiffBody>
      {changes.budgetTrend && (
        <DiffField>
          <K>budget_trend</K>
          <Old>On Target</Old>
          <New>{changes.budgetTrend}</New>
        </DiffField>
      )}
      {changes.oration && (
        <DiffField>
          <K>oration</K>
          <Old>— empty —</Old>
          <LongNew
            dangerouslySetInnerHTML={{
              __html: changes.oration
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.+?)\*/g, '<em>$1</em>')
                .split('\n\n')[0],
            }}
          />
        </DiffField>
      )}
      {changes.keyDates && (
        <DiffField>
          <K>key_dates</K>
          <New style={{ background: '#F4F4F4', color: '#676767' }}>
            {changes.keyDates.length} dates preserved · no changes
          </New>
        </DiffField>
      )}
    </DiffBody>
    <DiffActions>
      {submitted ? (
        <SubmittedRow>
          <I.check width={14} height={14} /> Submitted as Rev {producedRevLabel}
        </SubmittedRow>
      ) : (
        <>
          <SubBtn type="button" data-testid="wip-ai-submit-revision" onClick={onSubmit}>
            Submit revision <I.arrowRight width={12} height={12} />
          </SubBtn>
          <SecBtn type="button">Edit</SecBtn>
          <DiscBtn type="button" onClick={onDiscard}>
            Discard
          </DiscBtn>
        </>
      )}
    </DiffActions>
  </Diff>
)

WipAiDiffCard.propTypes = {
  changes: PropTypes.object.isRequired,
  producedRev: PropTypes.string,
  nextRevNumber: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  submitted: PropTypes.bool.isRequired,
  producedRevLabel: PropTypes.number,
}

export default WipAiDiffCard
